import { AxisCode, isAdmin, JobFunction, ValidationLane } from '@atlas/types'
import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isFromWorkfront } from '@/components/work-orders/WorkfrontBadges'
import { WorkOrderStepFooter } from '@/components/work-orders/WorkOrderStepFooter'
import { isChangeRequestResolved, laneChangeRequests } from '@/domain/change-requests'
import type { WorkOrder } from '@/domain/work-order.types'
import { statusLabel } from '@/domain/work-order.types'
import type { ChangeRequest } from '@/domain/workflow.types'
import { useAssignDesigner } from '@/hooks/useAssignDesigner'
import { useAuth } from '@/hooks/useAuth'
import { useBrandAndProduct } from '@/hooks/useBrandAndProduct'
import { useChangeRequests } from '@/hooks/useChangeRequests'
import { useBootstrapValues } from '@/hooks/useClassification'
import { useResolvedWorkflow } from '@/hooks/useResolvedWorkflow'
import { useUserNames } from '@/hooks/useUserNames'
import { useUsers } from '@/hooks/useUsers'
import { useWorkOrder } from '@/hooks/useWorkOrders'
import { withJobFunction } from '@/lib/option-order'
import { listPathFor } from '../WorkOrders/list-state'
import { WorkOrderCommentThread } from '../WorkOrders/WorkOrderCommentThread'
import { ChangeRequestAuthor } from '../WorkOrders/workspace/ChangeRequestAuthor'
import { ChangeRequestToggleList } from '../WorkOrders/workspace/ChangeRequestToggleList'
import { AssignDesignerCard, type DesignerOption } from './AssignDesignerCard'
import { CreatedStep } from './CreatedStep'
import { AssignedDesignerCard } from './complete-details/AssignedDesignerCard'
import { CompleteDetailsStep } from './complete-details/CompleteDetailsStep'
import { useCompleteDetails } from './complete-details/useCompleteDetails'
import { DistributionPackageUploads, useDistributionPackageFiles } from './DistributionPackageUploads'
import { PlaceholderStep } from './PlaceholderStep'
import { RequestDetailsSection } from './RequestDetailsSection'
import {
	DistributionPackageChoice,
	SourceMaterialsSection,
	type SourceMaterialsSectionProps,
} from './SourceMaterialsSection'
import { StageHeader, type StageMarket } from './StageHeader'
import { StageRail } from './StageRail'
import { designerOptions, flagEmoji, marketName, sourceMaterialCodes, veevaRelatedLink } from './stage-helpers'
import { CREATED_STATUS, DETAILS_STATUS, hasBuiltScreen, holdsTurn, mayPickPm, StageTurn, turnOf } from './stage-turn'
import { useStepActions } from './useStageCta'
import { SourceMaterialVerdict, VerifySourceMaterials } from './VerifySourceMaterials'

const LANE = ValidationLane.SOURCE_MATERIAL

/**
 * The designer's change requests: raised only once they find an issue, and
 * still shown while one from an earlier round awaits their sign-off.
 */
function DesignerChangeRequests({
	workOrderId,
	requests,
	verdict,
}: {
	workOrderId: string
	requests: readonly ChangeRequest[]
	verdict: SourceMaterialVerdict | null
}) {
	const isIssue = verdict === SourceMaterialVerdict.ISSUE
	if (!isIssue && requests.every(isChangeRequestResolved)) return null
	return (
		<ChangeRequestAuthor workOrderId={workOrderId} lane={LANE} currentLane={LANE} canAuthor showComposer={isIssue} />
	)
}

/** The order's market: its geography value's name and, for a country, its flag. */
function useMarket(workOrder: WorkOrder): StageMarket | null {
	const values = useBootstrapValues(AxisCode.GEOGRAPHY)
	const value = values.find((v) => v.code === workOrder.tuple?.[AxisCode.GEOGRAPHY]?.[0])
	if (!value) return null
	const iso = value.meta.iso_country_code
	return { name: marketName(value.label), flag: flagEmoji(typeof iso === 'string' ? iso : null) }
}

/** A choice made on the screen, dropped as soon as the order leaves the status it was made in. */
function useChoiceInStatus<T>(status: string): [T | null, (value: T) => void] {
	const [choice, setChoice] = useState<{ status: string; value: T } | null>(null)
	return [choice?.status === status ? choice.value : null, (value) => setChoice({ status, value })]
}

/**
 * What the order carries for the designer to review, and the PM's package choice: an order that
 * already carries package material starts on "Yes", otherwise the PM must answer. Only the PM, on
 * their turn, is asked the question and may upload package files.
 */
function useSourceMaterial(wo: WorkOrder, isPmTurn: boolean) {
	const [picked, setPicked] = useChoiceInStatus<DistributionPackageChoice>(wo.status)
	const codes = sourceMaterialCodes(wo)
	const fallbackUrl = veevaRelatedLink(wo)
	const uploadCount = useDistributionPackageFiles(wo.orderCode).length
	const hasPackage = codes.length > 0 || fallbackUrl !== null || uploadCount > 0
	const packageChoice = picked ?? (hasPackage ? DistributionPackageChoice.EXISTING : null)
	const section: SourceMaterialsSectionProps = {
		codes,
		fallbackUrl,
		packageChoice: isPmTurn ? { value: packageChoice, onChange: setPicked } : undefined,
		uploads: isPmTurn || uploadCount > 0 ? <DistributionPackageUploads workOrder={wo} canEdit={isPmTurn} /> : undefined,
	}
	return { hasPackage, packageChoice, section }
}

/** The incoming request, with its parent factory brief once that loads. */
function OrderRequestDetails({
	workOrder: wo,
	brand,
	market,
}: {
	workOrder: WorkOrder
	brand: string | null
	market: StageMarket | null
}) {
	const { data: parent } = useWorkOrder(wo.parentWorkOrderId)
	return (
		<RequestDetailsSection
			briefing={wo.briefing}
			externalDescription={isFromWorkfront(wo) ? wo.externalDescription : null}
			brand={brand}
			market={market?.name ?? null}
			parent={parent ? { workOrderId: parent.workOrderId, orderCode: parent.orderCode } : null}
			assetTypeCode={wo.assetTypeCode}
			complexityLevel={wo.complexityLevel}
		/>
	)
}

/** "Unassigned" only when nobody is: a set id reads as pending, then as unknown if it never resolves. */
function nameOf(userId: string | null | undefined, userNames: ReadonlyMap<string, string>, usersLoading: boolean) {
	if (!userId) return null
	if (usersLoading) return '…'
	return userNames.get(userId) ?? 'Unknown user'
}

/**
 * The PM's designer card: Sources verified draws it as the Full Path's Assigned designer, the
 * Stage 1 steps keep their Assign designer card until they migrate.
 */
function DesignerCard({
	isDetails,
	...card
}: {
	isDetails: boolean
	options: DesignerOption[]
	value: string | null
	onChange: (userId: string) => void
}) {
	return isDetails ? <AssignedDesignerCard {...card} /> : <AssignDesignerCard {...card} />
}

/** Who sees what on the order's current step. */
function stageFlags(wo: WorkOrder, user: ReturnType<typeof useAuth>['user']) {
	const isTurnHolder = holdsTurn(wo, user)
	const isBuilt = hasBuiltScreen(wo.status)
	const isCreated = wo.status === CREATED_STATUS
	const isDetails = wo.status === DETAILS_STATUS
	const turn = turnOf(wo.status)
	// The PM's turn past Created, where the designer card shows; Created has its own PM picker.
	const isPmTurn = isTurnHolder && isBuilt && turn === StageTurn.PM && !isCreated
	return {
		isTurnHolder,
		isBuilt,
		isCreated,
		isDetails,
		isPmTurn,
		// The source-material step's PM turn, where the package question and the change requests show.
		isSourcePmTurn: isPmTurn && !isDetails,
		isDesignerTurn: isTurnHolder && isBuilt && turn === StageTurn.DESIGNER,
		// Sources verified: the PM fills the details in, an administrator reads them.
		showsDetails: isDetails && (isTurnHolder || isAdmin(user?.role)),
	}
}

/** The step's own sections: the placeholder, Created, the details, or the source material. */
function StepBody({
	workOrder: wo,
	snapshot,
	flags,
	brand,
	product,
	market,
	details,
	section,
	otherMoves,
	stageLabel,
}: {
	workOrder: WorkOrder
	snapshot: ReturnType<typeof useResolvedWorkflow>['snapshot']
	flags: ReturnType<typeof stageFlags>
	brand: string | null
	product: string | null
	market: StageMarket | null
	details: ReturnType<typeof useCompleteDetails>
	section: SourceMaterialsSectionProps
	otherMoves: ReturnType<typeof useStepActions>['otherMoves']
	stageLabel: string
}) {
	const { user } = useAuth()
	if (!flags.isBuilt) return <PlaceholderStep stage={stageLabel} moves={otherMoves} />
	if (flags.isCreated) {
		return (
			<CreatedStep workOrder={wo} snapshot={snapshot} brand={brand} market={market} canPickPm={mayPickPm(wo, user)} />
		)
	}
	if (!flags.isDetails) return <SourceMaterialsSection {...section} />
	if (!flags.showsDetails) return null
	return (
		<div className="atlas-root space-y-6">
			<CompleteDetailsStep
				workOrder={wo}
				details={details}
				brand={brand}
				product={product}
				market={market}
				canEdit={flags.isTurnHolder}
			/>
		</div>
	)
}

function StageScreen({ workOrder: wo }: { workOrder: WorkOrder }) {
	const navigate = useNavigate()
	const { user } = useAuth()
	const { snapshot } = useResolvedWorkflow(wo)
	const { data: users, isLoading: usersLoading } = useUsers()
	const userNames = useUserNames()
	const { brand, product } = useBrandAndProduct(wo)
	const market = useMarket(wo)
	const [verdict, setVerdict] = useChoiceInStatus<SourceMaterialVerdict>(wo.status)
	const assignDesigner = useAssignDesigner(wo)
	const changeRequests = laneChangeRequests(useChangeRequests(wo.workOrderId).changeRequests, LANE)

	const flags = stageFlags(wo, user)
	const details = useCompleteDetails(wo, snapshot, flags.isDetails && flags.isTurnHolder)
	const { hasPackage, packageChoice, section } = useSourceMaterial(wo, flags.isSourcePmTurn)
	const { cta, otherMoves } = useStepActions(
		wo,
		snapshot,
		{ verdict, packageChoice, hasPackage, changeRequests },
		flags.isTurnHolder,
	)
	const status = snapshot?.statuses.find((s) => s.code === wo.status)
	const stageLabel = status?.label ?? statusLabel(wo.status)

	return (
		<div>
			<StageHeader title={wo.title} orderCode={wo.orderCode} dueAt={wo.dueAt} />
			<div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_24.5rem]">
				<div className="min-w-0 space-y-6">
					{/* Sources verified shows the request as Task Details, and only to whoever completes or
					    reads the details: the Full Path draws no request card for anyone else. */}
					{flags.isDetails ? null : <OrderRequestDetails workOrder={wo} brand={brand ?? null} market={market} />}
					<StepBody
						workOrder={wo}
						snapshot={snapshot}
						flags={flags}
						brand={brand ?? null}
						product={product ?? null}
						market={market}
						details={details}
						section={section}
						otherMoves={otherMoves}
						stageLabel={stageLabel}
					/>
					{flags.isPmTurn ? (
						<DesignerCard
							isDetails={flags.isDetails}
							options={designerOptions(withJobFunction(users, JobFunction.DESIGNER, wo.assigneeIds[0]))}
							value={wo.assigneeIds[0] ?? null}
							onChange={(userId) => assignDesigner(userId, userNames.get(userId))}
						/>
					) : null}
					{flags.isSourcePmTurn ? (
						<ChangeRequestToggleList
							workOrderId={wo.workOrderId}
							lane={LANE}
							changeRequests={changeRequests}
							currentStatus={wo.status}
						/>
					) : null}
					{flags.isDesignerTurn ? (
						<>
							<VerifySourceMaterials value={verdict} onChange={setVerdict} />
							<DesignerChangeRequests workOrderId={wo.workOrderId} requests={changeRequests} verdict={verdict} />
						</>
					) : null}
					<SectionCard title="Comments" collapsible>
						<WorkOrderCommentThread workOrder={wo} />
					</SectionCard>
				</div>
				<div className="space-y-4 xl:sticky xl:top-4">
					<StageRail
						stage={{ label: stageLabel, phase: status?.phase ?? null }}
						designerName={nameOf(wo.assigneeIds[0], userNames, usersLoading)}
						pmName={nameOf(wo.pmUserId, userNames, usersLoading)}
					/>
				</div>
			</div>
			<WorkOrderStepFooter
				onBack={() => void navigate(listPathFor(wo.kind))}
				cta={flags.isDetails ? details.cta : cta}
				secondary={flags.isDetails ? details.secondary : null}
			/>
		</div>
	)
}

/** `/work-orders/:id`: the order's current step, the one screen its PM and designer share. */
export function WorkOrderStagePage() {
	const { id } = useParams<{ id: string }>()
	const { data: workOrder, isLoading } = useWorkOrder(id ?? null)

	if (isLoading) return <ListSkeleton label="Loading…" />
	if (!workOrder) return <p className="text-ds-text-muted">Work order not found.</p>
	return <StageScreen workOrder={workOrder} />
}
